import { ControlProps, OwnPropsOfEnum } from '@jsonforms/core';
import { MaterialInputControl, MuiSelect, WithOptionLabel } from '@jsonforms/material-renderers';
import { TranslateProps } from '@jsonforms/react';
import merge from 'lodash/merge';
import { useContext, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { MuiAutocomplete } from '../copyConfiguration/component';
import { EditorContext } from '../../context';
import { factoryManagementActions } from '../../../../../store/reducers/factory-management';

interface ControllerTypes {
  factoriesList: { factory_id: number | string; factory_name: string }[];
}

const SelectFactoryConfigurationControl = (
  props: ControlProps & OwnPropsOfEnum & WithOptionLabel & TranslateProps & ControllerTypes
) => {
  const { factoryId } = useParams();
  const { activeComponentId, activeNode, updateData } = useContext(EditorContext);
  const currentOpt = activeNode?.data?.options?.find((opt: any) => opt.component_id === activeComponentId);
  const shouldDefaultToActiveFactory = currentOpt?.operation === 'added' && props.data === undefined;

  const [value, setValue] = useState<string | undefined>(
    shouldDefaultToActiveFactory ? String(factoryId ?? '') : props.data
  );
  const dispatch = useDispatch();
  const { config, uischema, errors, factoriesList } = props;
  const appliedUiSchemaOptions = merge({}, config, uischema.options);
  const isValid = errors.length === 0;

  useEffect(() => {
    setValue(shouldDefaultToActiveFactory ? String(factoryId ?? '') : props.data);
  }, [props.data, shouldDefaultToActiveFactory, factoryId]);

  const factoryOptions = useMemo(
    () =>
      (factoriesList ?? []).map((factory) => ({
        label: factory.factory_name,
        value: String(factory.factory_id),
      })),
    [factoriesList]
  );

  return appliedUiSchemaOptions.autocomplete === false ? (
    <MaterialInputControl {...props} input={MuiSelect} />
  ) : (
    <MuiAutocomplete
      {...props}
      data={value}
      isValid={isValid}
      onOpen={() => {}}
      options={factoryOptions}
      handleChange={(path, selectedValue) => {
        const normalizedValue = selectedValue ?? '';
        setValue(normalizedValue);
        const hasFactoryChanged =
          normalizedValue !== String(currentOpt?.data?.select_factory_to_copy_configuration_from ?? '');
        if (hasFactoryChanged) {
          updateData({
            ...activeNode.data,
            options: activeNode.data.options.map((opt: any) => {
              if (opt.component_id !== activeComponentId) return opt;
              return { ...opt, data: { select_factory_to_copy_configuration_from: normalizedValue } };
            }),
          });
          props.handleChange('copy_configuration_from', undefined);
          dispatch(factoryManagementActions.clearCopyConfigurationFromComponent());
        }
        props.handleChange(path, normalizedValue);
      }}
    />
  );
};

const mapStateToProps = (state: any) => ({
  factoriesList: state.factoryManagement.factoriesList,
});

export default connect(mapStateToProps)(SelectFactoryConfigurationControl);
